/** Secret user toggle for portal assistant UI (sidenav + floating dock). */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "evp_asst_ui";
const VISIBILITY_EVENT = "evp-assistant-visibility";

function readStoredFlag() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === "1") return true;
  if (raw === "0") return false;
  return null;
}

/** Feature compiled into this build (env flag). */
export function isAssistantBuildEnabled() {
  return process.env.REACT_APP_PORTAL_ASSISTANT_ENABLED === "true";
}

/** User preference — defaults visible unless the user explicitly hides it. */
export function getAssistantUserEnabled() {
  const stored = readStoredFlag();
  if (stored !== null) return stored;
  return true;
}

export function setAssistantUserEnabled(enabled) {
  localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  window.dispatchEvent(new CustomEvent(VISIBILITY_EVENT, { detail: { enabled: Boolean(enabled) } }));
}

export function toggleAssistantUserEnabled() {
  const next = !getAssistantUserEnabled();
  setAssistantUserEnabled(next);
  return next;
}

export function isAssistantVisible() {
  return isAssistantBuildEnabled() && getAssistantUserEnabled();
}

export function subscribeAssistantVisibility(listener) {
  const onChange = (event) => {
    listener(event?.detail?.enabled ?? getAssistantUserEnabled());
  };
  window.addEventListener(VISIBILITY_EVENT, onChange);
  return () => window.removeEventListener(VISIBILITY_EVENT, onChange);
}

export function useAssistantVisibility() {
  const [userEnabled, setUserEnabled] = useState(getAssistantUserEnabled);

  useEffect(() => subscribeAssistantVisibility(setUserEnabled), []);

  const buildEnabled = isAssistantBuildEnabled();
  const visible = buildEnabled && userEnabled;

  const toggle = useCallback(() => {
    if (!buildEnabled) return false;
    return toggleAssistantUserEnabled();
  }, [buildEnabled]);

  return {
    buildEnabled,
    userEnabled,
    visible,
    toggle,
    setEnabled: setAssistantUserEnabled,
  };
}

/** Triple-click handler factory (Settings easter egg). */
export function createSecretTripleClickHandler(onUnlock, { windowMs = 900 } = {}) {
  let count = 0;
  let timer = null;

  return () => {
    count += 1;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      count = 0;
      timer = null;
    }, windowMs);

    if (count >= 3) {
      count = 0;
      if (timer) clearTimeout(timer);
      timer = null;
      onUnlock();
    }
  };
}
